// React
import React, { useEffect, useMemo, useState } from 'react';

// Mantine
import {
  Autocomplete,
  AutocompleteProps,
  Group,
  Highlight,
  Loader,
  OptionsFilter,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

import { useSearchTariffs } from 'api/resource';

import { useActiveForm } from 'pages/claims/components';

import { getValueByPath } from 'utils/getValueByPath';

// Interface
import { Payor, Provider, Tariff } from 'interfaces';

interface TariffSearchProps {
  dataRowID?: string;
  defaultValue?: string;
  onChange: (tariffID: string) => void;
}

export const TariffSearch: React.FC<TariffSearchProps> = ({
  dataRowID,
  onChange,
  defaultValue = '',
}) => {
  const NO_INFO = '__NO_INFO__';
  const NO_INFO_MESSAGE = `Tariff does not have a specialtyCode "ALL". Select a provider to search specialty-specific tariffs.`;

  const { form, prefix } = useActiveForm();
  const providerPath = prefix ? `${prefix}.provider` : 'provider';
  const serviceStartPath = prefix ? `${prefix}.serviceStart` : 'serviceStart';
  const payorObjectPath = prefix ? `${prefix}.payor` : 'payor';

  const provider: Provider | undefined = getValueByPath(
    form.getValues(),
    providerPath
  );
  const serviceStart: string | undefined = getValueByPath(
    form.getValues(),
    serviceStartPath
  );
  const payor: Payor | undefined = getValueByPath(
    form.getValues(),
    payorObjectPath
  );
  const specialtyCode = provider?.specialtyCode || undefined;
  const province = payor?.province || 'MB';

  const [searchValue, setSearchValue] = useState<string | null>('');
  const [inputValue, setInputValue] = useState<string>(
    defaultValue === '0' ? '' : defaultValue
  );
  const [debounced] = useDebouncedValue<string | null>(searchValue.trim(), 500);

  const computedColorScheme = useComputedColorScheme('dark');
  const color = computedColorScheme === 'dark' ? 'white' : 'black';
  const {
    data: tariffs = [],
    isLoading,
    isFetching,
    refetch,
  } = useSearchTariffs({
    searchValue: debounced,
    specialtyCode,
    serviceStart,
    province,
  });

  useEffect(() => {
    refetch();
  }, [debounced, specialtyCode, province]);

  useEffect(() => {
    setInputValue(defaultValue === '0' ? '' : defaultValue);
  }, [defaultValue]);

  const showNoTariffMessage =
    debounced.length > 0 && !isLoading && !isFetching && tariffs.length === 0;

  const options = useMemo(() => {
    if (showNoTariffMessage) {
      const msg = specialtyCode
        ? `No tariffs found for specialty "${specialtyCode}" and none with "ALL".`
        : NO_INFO_MESSAGE;
      return [{ value: NO_INFO, label: msg, disabled: true }];
    }

    return tariffs.map((t) => ({
      value: JSON.stringify(t),
      label: `${t.prefix}${t.code} - ${t.description}`,
    }));
  }, [showNoTariffMessage, tariffs, specialtyCode]);

  const renderAutocompleteOption: AutocompleteProps['renderOption'] = ({
    option,
  }: {
    option: { value: string; label: string } | null;
  }) => {
    if (!option) return <Loader />;

    if (option.value === NO_INFO) {
      return (
        <Text size="sm" c="dimmed">
          {option.label}
        </Text>
      );
    }
    let value: Tariff | null = null;
    try {
      value = JSON.parse(option.value);
    } catch {
      return <Text size="sm">No results</Text>;
    }
    if (!value) return <Text size="sm">No results</Text>;

    return (
      <Group gap="xs">
        <div>
          <Text size="sm">
            <Highlight highlight={searchValue}>
              {`${value.prefix}${value.code}`}
            </Highlight>
          </Text>
          <Group gap="xs">
            <Text size="xs" opacity={0.5}>
              Description:
            </Text>
            <Highlight size="xs" highlight={searchValue}>
              {value.description ?? ''}
            </Highlight>
          </Group>
        </div>
      </Group>
    );
  };
  const optionsFilter: OptionsFilter = ({ options }) => options;
  return (
    <Autocomplete
      comboboxProps={{
        withinPortal: true,
        position: 'bottom-start',
        width: 'lg',
      }}
      value={inputValue}
      style={{
        width: '90%',
      }}
      radius="md"
      size="sm"
      data-testid="tariff-search-autocomplete"
      data-tariff-row-id={dataRowID}
      c={color}
      data={options}
      renderOption={renderAutocompleteOption}
      onChange={(value) => {
        setInputValue(value);
        if (value !== defaultValue) setSearchValue(value);
      }}
      onOptionSubmit={(value) => {
        if (value === NO_INFO) return;
        onChange(value);
        setSearchValue('');
        try {
          const selected = JSON.parse(value) as Tariff;
          setInputValue(`${selected.prefix}${selected.code}`);
        } catch {
          setInputValue(defaultValue);
        }
      }}
      filter={optionsFilter}
      rightSection={
        (isLoading || isFetching) && (
          <Loader
            style={{ position: 'absolute', right: 15, top: 10 }}
            size={20}
            color={color}
          />
        )
      }
    />
  );
};
